import React, { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  CmpPrimaryMemberCredentialsActionEnum, CmpPrimaryMemberCredentialsExclusionReasonEnum, useGetCmpPrimaryMemberCredentialsLastSentQuery,
  useGetCmpPrimaryMemberCredentialsPlanLazyQuery, useIssueCmpPrimaryMemberCredentialsMutation,
} from "../../../../graphql/types";
import SendCredentialsDialog from "../UpdateMember/SendCredentialsDialog";
import { buildMemberCredentialEmailPreviewDocument } from "../UpdateMember/memberCredentialEmailPreviewDocument";
import { DesignDialog, DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import { memberRequestApolloContext } from "../memberWorkflowShared";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import MassCredentialEmailEditor, { MassCredentialDiscardConfirm, MassCredentialEmailEditorHandle } from "./MassCredentialEmailEditor";
import {
  areMassCredentialEmailEditsEqual,
  loadMassCredentialEmailEdits,
  MassCredentialEmailEdits,
  MassCredentialEmailEditsUnavailableError,
  saveMassCredentialEmailEdits,
  validateMassCredentialEmailEdits,
} from "./massCredentialEmailOverrides";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
} from "../../../../utils/userFacingError";

const csvCell = (value: string) => {
  const safe = /^[\s]*[=+@-]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
};

// The credential email links to this dashboard (backend memberPortalLink in
// cmpPrimaryMemberCredentials.ts). Use a plan login URL when the API has one.
export const massCredentialLoginUrl = "https://dashboard.chamberperksapp.com";
export const massCredentialCsvFilename = "member-dashboard-credentials.csv";
export const massCredentialsSentMessage = "Member Dashboard credentials have been sent to all members who have not logged in yet!";
export const massCredentialEmailSavedMessage = "Your email changes have been saved.";
export const getMassCredentialsDownloadedMessage = (count: number) =>
  `Login credentials for ${count} Primary ${count === 1 ? "Contact" : "Contacts"} have been downloaded successfully.`;

const lastSentDateFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const lastSentTimeFormatter = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
/** Design format: "Jul 14, 2026 at 9:30 AM" in the viewer's time zone. */
export const formatMassCredentialLastSent = (utcMilli: number) => {
  const date = new Date(utcMilli);
  return `${lastSentDateFormatter.format(date)} at ${lastSentTimeFormatter.format(date)}`;
};

// M3: each download is one server reset. Run a few at a time; a larger
// number adds provider throttling risk for little gain. Send stays one at a
// time so it stops at the first uncertain email.
export const MASS_CREDENTIAL_DOWNLOAD_CONCURRENCY = 4;

/** "Preparing credentials: 12 of 424 (about 3 min left)". The estimate uses the measured time per member so far. */
export const formatMassCredentialProgress = ({ action, done, total, elapsedMs }: {
  action: CmpPrimaryMemberCredentialsActionEnum; done: number; total: number; elapsedMs: number;
}) => {
  const label = `${action === CmpPrimaryMemberCredentialsActionEnum.Send ? "Sending" : "Preparing"} credentials: ${done} of ${total}`;
  if (!done || done >= total) return label;
  const minutesLeft = (elapsedMs / done) * (total - done) / 60000;
  return `${label} (${minutesLeft < 1 ? "less than 1 min left" : `about ${Math.ceil(minutesLeft)} min left`})`;
};

// M2: short labels for why members are not in the recipient list.
export const massCredentialExclusionLabels: Record<CmpPrimaryMemberCredentialsExclusionReasonEnum, string> = {
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.Suspended]: "Suspended",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.Pending]: "Pending",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.Past]: "Past members",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.NotActive]: "Other inactive statuses",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.NoPrimaryEmail]: "No Primary Contact email",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.PrimaryEmailDiffersFromLogin]: "Primary Contact email is not the member's sign-in email",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.AlreadyLoggedIn]: "Already logged in",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.CredentialsAlreadySent]: "Credentials sent in the last 24 hours",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.NoDashboardAccount]: "No Member Dashboard account",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.OpenAccessClaim]: "New member access is still in progress",
  [CmpPrimaryMemberCredentialsExclusionReasonEnum.Other]: `Other reasons (email ${STAFF_SUPPORT_EMAIL} to check)`,
};

type MassCredentialExclusionSummaryProps = {
  recipientCount: number;
  skippedCount: number;
  skippedReasons: Array<{ reason: CmpPrimaryMemberCredentialsExclusionReasonEnum; count: number }>;
};

// Monday 12972906938 (Oct 16 2026): the design pop-up has no included /
// excluded box. The counts and reasons sit inside "View all recipients".
const MassCredentialExclusionSummary = ({ recipientCount, skippedCount, skippedReasons }: MassCredentialExclusionSummaryProps) => (
  <div>
    <p><strong>{recipientCount}</strong> Primary {recipientCount === 1 ? "Contact is" : "Contacts are"} included. <strong>{skippedCount}</strong> {skippedCount === 1 ? "member is" : "members are"} excluded{skippedReasons.length ? ":" : "."}</p>
    {skippedReasons.length > 0 && <ul className="list-disc pl-[20px]">
      {skippedReasons.map(row => <li key={row.reason}>{massCredentialExclusionLabels[row.reason]}: {row.count}</li>)}
    </ul>}
  </div>
);

type SuccessPopup = { message: string; note?: string };
type EditState = { base: MassCredentialEmailEdits; resetKey: number };

export type MassMemberCredentialsProps = {
  chamberId: string;
  chamberName: string;
};

export default function MassMemberCredentials({ chamberId, chamberName }: MassMemberCredentialsProps) {
  const noun = useCmpOrganizationNoun();
  const [mode, setMode] = useState<"closed" | "options" | "preview">("closed");
  // Q9 (Moeen, Oct 16 2026; Not in design): confirm Send to All and Download Credentials
  // when some contacts were sent credentials before (both give a new temporary password).
  const [confirmAction, setConfirmAction] = useState<CmpPrimaryMemberCredentialsActionEnum | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessPopup | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [discardAction, setDiscardAction] = useState<"cancel" | "close" | null>(null);
  const editorRef = useRef<MassCredentialEmailEditorHandle>(null);
  const editResetCounter = useRef(0);
  const [progress, setProgress] = useState<{ action: CmpPrimaryMemberCredentialsActionEnum; done: number; total: number; elapsedMs: number } | null>(null);
  const running = useRef(false);
  // Keep the member boundary stable when a fresh plan has a new identity hash.
  // A rejected request can still have changed a password or reached the provider.
  const attempted = useRef(new Set<string>());
  const [outcomes, setOutcomes] = useState<Array<{ chamberId: string; memberId: string; name: string; status: "completed" | "uncertain" }>>([]);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [confirmedSend, setConfirmedSend] = useState<{ chamberId: string; at: number } | null>(null);
  const planRequestRunning = useRef(false);
  const [planRequestFailed, setPlanRequestFailed] = useState(false);
  const [loadedChamberId, setLoadedChamberId] = useState<string | null>(null);
  const client = useApolloClient();
  const [loadPlan, { data, loading, error, variables }] = useGetCmpPrimaryMemberCredentialsPlanLazyQuery({
    fetchPolicy: "network-only", notifyOnNetworkStatusChange: true, context: memberRequestApolloContext,
  });
  // One cheap read for "Last sent"; the full plan loads when the pop-up opens.
  const lastSentQuery = useGetCmpPrimaryMemberCredentialsLastSentQuery({
    variables: { chamberId }, skip: !chamberId, fetchPolicy: "network-only", context: memberRequestApolloContext,
  });
  const [issue] = useIssueCmpPrimaryMemberCredentialsMutation({ fetchPolicy: "no-cache", ignoreResults: true, context: memberRequestApolloContext });
  const planFailed = !!error || planRequestFailed;
  // Apollo can retain the preceding result during a new chamber request.
  // Never use that result for another chamber's credential actions.
  const plan = !loading && loadedChamberId === chamberId && variables?.chamberId === chamberId && !planFailed ? data?.getCmpPrimaryMemberCredentialsPlan : undefined;
  const recipients = (plan?.recipients || []).filter(recipient => !attempted.current.has(`${chamberId}:${recipient.memberId}`));
  const previouslySent = recipients.filter(recipient => !!recipient.previouslySentAtUtcMilli).length;
  const lastSentFromQuery = lastSentQuery.variables?.chamberId === chamberId
    ? lastSentQuery.data?.getCmpPrimaryMemberCredentialsLastSent.lastSentAtUtcMilli || 0 : 0;
  const lastSentAt = Math.max(plan?.lastSentAtUtcMilli || 0, lastSentFromQuery, confirmedSend?.chamberId === chamberId ? confirmedSend.at : 0);
  const protectedOutcomes = outcomes.filter(outcome => outcome.chamberId === chamberId);
  const unavailable = busy || loading || planFailed || !recipients.length;
  const disabled = unavailable || reviewRequired;
  const previewFailure = !loading && plan && (!plan.emailHtml.trim() || !plan.subject.trim() || !plan.fromEmail.trim())
    ? "The email preview or sender could not be loaded. Close this window and try again." : null;
  const sendDisabled = disabled || !!previewFailure || !plan || !!editing || editBusy;
  const recordOutcome = (memberId: string, name: string, status: "completed" | "uncertain") => {
    setOutcomes(current => [...current.filter(outcome => outcome.chamberId !== chamberId || outcome.memberId !== memberId), { chamberId, memberId, name, status }]);
  };
  const refreshPlan = async () => {
    if (planRequestRunning.current || running.current || !chamberId) return false;
    planRequestRunning.current = true;
    setPlanRequestFailed(false);
    setLoadedChamberId(null);
    try {
      const result = await loadPlan({ variables: { chamberId } });
      const failed = !!result.error || !result.data?.getCmpPrimaryMemberCredentialsPlan;
      setPlanRequestFailed(failed);
      if (!failed) setLoadedChamberId(chamberId);
      return !failed;
    } catch {
      setPlanRequestFailed(true);
      return false;
    } finally { planRequestRunning.current = false; }
  };
  // A chamber change closes any open preview so no action uses the previous
  // chamber's plan. "Last sent" comes from the lightweight query above.
  useEffect(() => {
    setMode("closed"); setEditing(null); setEditError(null); setDiscardAction(null);
  }, [chamberId]);
  const open = async () => {
    setMode("options");
    await refreshPlan();
  };
  const leaveEditing = () => { setEditing(null); setEditError(null); setDiscardAction(null); };
  const hasUnsavedEdits = () => {
    const current = editorRef.current?.readEdits();
    return !!editing && !!current && !areMassCredentialEmailEditsEqual(editing.base, current);
  };
  const startEditing = async () => {
    if (editBusy || busy) return;
    setEditBusy(true); setEditError(null);
    try {
      const base = await loadMassCredentialEmailEdits({ chamberId, client });
      editResetCounter.current += 1;
      setEditing({ base, resetKey: editResetCounter.current });
    } catch (loadError) {
      setEditError(loadError instanceof MassCredentialEmailEditsUnavailableError ? loadError.message : getUserFacingErrorText(loadError, buildStaffErrorFallback("load the email text")));
    } finally { setEditBusy(false); }
  };
  const cancelEditing = () => {
    if (editBusy) return;
    if (hasUnsavedEdits()) setDiscardAction("cancel");
    else leaveEditing();
  };
  const saveEditing = async () => {
    if (!editing || editBusy) return;
    const edits = editorRef.current?.readEdits() || editing.base;
    if (areMassCredentialEmailEditsEqual(editing.base, edits)) { leaveEditing(); return; }
    const invalid = validateMassCredentialEmailEdits(edits);
    if (invalid) { setEditError(invalid); return; }
    setEditBusy(true); setEditError(null);
    try {
      await saveMassCredentialEmailEdits({ chamberId, client, edits });
      leaveEditing();
      setSuccess({ message: massCredentialEmailSavedMessage });
      // The saved text changes each recipient's identity hash. Reload the
      // plan so the preview and the next send use the saved email.
      await refreshPlan();
    } catch (saveError) {
      setEditError(saveError instanceof MassCredentialEmailEditsUnavailableError ? saveError.message : getUserFacingErrorText(saveError, buildStaffErrorFallback("save your email changes")));
    } finally { setEditBusy(false); }
  };
  const discardEdits = () => {
    const closeAfter = discardAction === "close";
    leaveEditing();
    if (closeAfter) setMode("closed");
  };
  const closePreview = () => {
    if (busy || editBusy || success) return;
    if (discardAction) { setDiscardAction(null); return; }
    if (editing && hasUnsavedEdits()) { setDiscardAction("close"); return; }
    leaveEditing();
    setMode("closed");
  };
  const reviewRemaining = async () => {
    if (running.current) return;
    setBusy(true);
    try {
      if (await refreshPlan()) {
        setReviewRequired(false);
        setMode("preview");
      }
    } finally { setBusy(false); }
  };
  const requestAction = (action: CmpPrimaryMemberCredentialsActionEnum) => {
    if (previouslySent > 0) { setConfirmAction(action); return; }
    void run(action);
  };
  const requestSend = () => requestAction(CmpPrimaryMemberCredentialsActionEnum.Send);
  const confirmingDownload = confirmAction === CmpPrimaryMemberCredentialsActionEnum.Download;

  const run = async (action: CmpPrimaryMemberCredentialsActionEnum) => {
    if (running.current || planRequestRunning.current || disabled || !plan || (action === CmpPrimaryMemberCredentialsActionEnum.Send && sendDisabled)) return;
    running.current = true; setBusy(true); setMessage(null); setSuccess(null);
    const batch = [...recipients];
    const startedAt = Date.now();
    setProgress({ action, done: 0, total: batch.length, elapsedMs: 0 });
    const csv = [["Member", "Primary Contact", "Email/Username", "Temporary Password", "Login URL"]];
    // Rows by batch position, so the CSV keeps the plan order when several
    // requests run at once. Every returned password must reach the CSV.
    const rows: Array<string[] | undefined> = [];
    let completed = 0;
    let downloaded = 0;
    let failure: string | null = null;
    let nextIndex = 0;
    const earlierUncertain = protectedOutcomes.some(outcome => outcome.status === "uncertain");
    const fail = (reason: string) => { failure = failure || reason; };
    const processNext = async (): Promise<void> => {
      // Stop starting new members after the first problem. Requests already
      // started finish, and their results are kept.
      while (!failure && nextIndex < batch.length) {
        const index = nextIndex;
        nextIndex += 1;
        const recipient = batch[index];
        attempted.current.add(`${chamberId}:${recipient.memberId}`);
        recordOutcome(recipient.memberId, recipient.contactName, "uncertain");
        let outcome;
        try {
          const result = await issue({ variables: { input: {
            chamberId, memberId: recipient.memberId, expectedIdentity: recipient.expectedIdentity, action,
          } } });
          outcome = result.data?.issueCmpPrimaryMemberCredentials;
        } catch {
          fail(`We could not confirm what happened for ${recipient.contactName}. We did not try again. Email ${STAFF_SUPPORT_EMAIL} before you send or download credentials for this contact again.`);
          return;
        }
        if (!outcome?.completed || outcome.alreadyProcessed) {
          if (outcome?.completed) recordOutcome(recipient.memberId, recipient.contactName, "completed");
          // outcome.warning is server text for staff and is shown as it is.
          fail(outcome?.warning || `We could not confirm that credentials reached this contact. Do not try again. Email ${STAFF_SUPPORT_EMAIL} to check this contact.`);
          return;
        }
        recordOutcome(recipient.memberId, recipient.contactName, "completed");
        if (action === CmpPrimaryMemberCredentialsActionEnum.Send &&
          typeof outcome.sentAtUtcMilli === "number" && Number.isFinite(outcome.sentAtUtcMilli) && outcome.sentAtUtcMilli > 0) {
          const at = outcome.sentAtUtcMilli;
          setConfirmedSend(previous => ({ chamberId, at: Math.max(at, previous?.chamberId === chamberId ? previous.at : 0) }));
        }
        if (action === CmpPrimaryMemberCredentialsActionEnum.Download) {
          if (!outcome.password) {
            recordOutcome(recipient.memberId, recipient.contactName, "uncertain");
            fail(`A new password was set for ${recipient.contactName}, but we did not get it, so it is not in the file. Do not try again. Email ${STAFF_SUPPORT_EMAIL} to check this contact.`);
            return;
          }
          rows[index] = [recipient.memberName, recipient.contactName, outcome.email || recipient.email, outcome.password, plan?.loginUrl || massCredentialLoginUrl];
        }
        completed += 1;
        setProgress({ action, done: completed, total: batch.length, elapsedMs: Date.now() - startedAt });
        if (outcome.warning) { fail(`${recipient.contactName}: ${outcome.warning}`); return; }
      }
    };
    try {
      const workers = action === CmpPrimaryMemberCredentialsActionEnum.Download ? MASS_CREDENTIAL_DOWNLOAD_CONCURRENCY : 1;
      await Promise.all(Array.from({ length: Math.min(workers, batch.length) }, () => processNext()));
      rows.forEach(row => { if (row) csv.push(row); });
    } finally {
      // Passwords stay in this operation only. The mutation uses no-cache.
      // Do not retain secrets for a retry or put them in state or storage.
      try {
        if (csv.length > 1) {
          downloadTextFile({ filename: massCredentialCsvFilename, contentType: "text/csv;charset=utf-8", content: csv.map(row => row.map(csvCell).join(",")).join("\r\n") });
          downloaded = csv.length - 1;
        }
      } catch {
        batch.forEach((recipient, index) => { if (rows[index]) recordOutcome(recipient.memberId, recipient.contactName, "uncertain"); });
        failure = `The download file did not start. New passwords were already set for these contacts, so the credentials sent to them before no longer work. Do not try again. Email ${STAFF_SUPPORT_EMAIL} for help.`;
      } finally {
        csv.forEach(row => row.fill("")); csv.length = 0;
        rows.forEach(row => row?.fill("")); rows.length = 0;
        const sent = action === CmpPrimaryMemberCredentialsActionEnum.Send;
        const resultMessage = sent
          ? `Credentials sent to ${completed} of ${batch.length} contacts.`
          : downloaded ? `Download started for ${downloaded} of ${batch.length} contacts.`
            : "No file was downloaded.";
        if (failure || (!sent && !downloaded)) {
          // Partial and uncertain results stay visible next to the action.
          setMessage(`${resultMessage}${failure ? ` ${failure}` : ""}`);
        } else {
          const notes = [
            earlierUncertain ? `Some contacts tried earlier are not confirmed yet. Email ${STAFF_SUPPORT_EMAIL} to check them.` : "",
          ].filter(Boolean);
          setSuccess({
            message: sent ? massCredentialsSentMessage : getMassCredentialsDownloadedMessage(downloaded),
            note: notes.length ? notes.join(" ") : undefined,
          });
        }
        setReviewRequired(!!failure);
        setMode(failure ? "preview" : "closed");
        setBusy(false); setProgress(null); running.current = false;
        // A fresh plan is requested only when staff reopen or review the remaining contacts.
      }
    }
  };
  const failure = planFailed ? "The recipient list could not be loaded. Try loading it again. This does not send email or change credentials." : !loading && plan && !recipients.length ? protectedOutcomes.length ? "Every contact in this list was already tried, or needs a check by support. There is no one left to send to." : "No Primary Contacts can get credentials right now. A member is left out if it is not Active, its Primary Contact already signed in or has no email, or credentials were sent in the last 24 hours." : null;
  const lastSentLabel = lastSentAt
    ? formatMassCredentialLastSent(lastSentAt)
    : lastSentQuery.loading || loading ? "Loading..." : lastSentQuery.error && !plan ? "Unavailable" : "Not yet sent";
  return <div className="flex max-w-full flex-col items-start gap-[8px] sm:items-end">
    <DesignDialogButton tone="green" className="!flex-none" onClick={open} disabled={!chamberId || busy}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
      Send Mass Member Credentials
    </DesignDialogButton>
    <p className="text-[12px] leading-[1.5] text-[#727272] sm:text-right">Will only be sent to the member’s Primary Contact who has not yet logged into their Member Dashboard.<br />Last sent: {lastSentLabel}</p>
    {protectedOutcomes.length > 0 && <details className="max-w-[520px] text-[13px]"><summary>{protectedOutcomes.length} contacts were already tried and will be skipped</summary><ul>{protectedOutcomes.map(outcome => <li key={outcome.memberId}>{outcome.name}: {outcome.status === "completed" ? "completed" : `not confirmed, email ${STAFF_SUPPORT_EMAIL} to check`}</li>)}</ul></details>}
    {reviewRequired && <DesignDialogButton tone="dark" disabled={unavailable} onClick={reviewRemaining}>Review Remaining Contacts</DesignDialogButton>}
    {message && <p role="status" className="max-w-[520px] text-[13px] text-[#0A0A0A]">{message}</p>}
    <DesignDialog open={mode === "options" && planFailed && !loading} onClose={() => setMode("closed")} size="wide" ariaLabel="Recipient list unavailable">
      <h2 className="text-[18px] font-bold">Recipient list unavailable</h2>
      <p role="alert" className="text-[14px] leading-[1.5]">{failure}</p>
      {message && <p role="status" className="text-[13px]">{message}</p>}
      <DesignDialogButton disabled={loading || busy} onClick={() => { void refreshPlan(); }}>Try Loading Again</DesignDialogButton>
    </DesignDialog>
    <DesignDialog open={confirmAction !== null} onClose={() => setConfirmAction(null)} icon="warning" title={confirmingDownload ? "DOWNLOAD CREDENTIALS" : "SEND TO ALL"}
      message={confirmingDownload
        ? `${previouslySent} of the ${recipients.length} Primary Contacts were sent credentials before and have not logged in yet. Download Credentials gives them a new temporary password, so the credentials already sent to them stop working.`
        : `${previouslySent} of the ${recipients.length} Primary Contacts were sent credentials before and have not logged in yet. Send to All emails them again with a new temporary password.`}
      actions={<><DesignDialogButton disabled={busy} onClick={() => { const action = confirmAction; setConfirmAction(null); if (action) void run(action); }}>{confirmingDownload ? "Download Credentials" : "Send to All"}</DesignDialogButton><DesignDialogButton tone="dark" disabled={busy} onClick={() => setConfirmAction(null)}>Cancel</DesignDialogButton></>} />
    <SendCredentialsDialog open={mode === "options" && (!planFailed || loading)} mass mode="options" loading={loading || busy} busy={busy} loadingLabel={busy ? progress ? `${formatMassCredentialProgress(progress)}. Keep this page open.` : "Preparing credentials..." : "Loading primary contacts..."} disabled={disabled} previewDisabled={sendDisabled}
      error={failure || previewFailure || message} subject={plan?.subject || ""} html={plan?.emailHtml || ""} recipientName="Primary Contacts" recipientEmail={recipients[0]?.email || ""}
      onDownloadCsv={() => requestAction(CmpPrimaryMemberCredentialsActionEnum.Download)} onPreview={() => setMode("preview")}
      onDownloadEmail={() => setMode("preview")} onSend={() => requestSend()} onClose={() => !busy && setMode("closed")} />
    <DesignDialog open={mode === "preview"} onClose={closePreview} busy={busy || editBusy} size="wide" ariaLabel="Member Dashboard Welcome Email Preview" className="h-[90dvh] !w-[min(700px,calc(100vw-48px))] !max-h-[90dvh] !items-stretch !overflow-hidden !border-[#ECECEC] !p-0 !text-left [&>button]:z-[1]">
      <div className="flex min-h-0 flex-1 flex-col gap-[16px] overflow-hidden px-[32px] py-[28px]">
        <div className="flex shrink-0 flex-col gap-[6px] pr-[40px]">
          <h2 className="text-[18px] font-bold leading-[normal] text-[#0A0A0A]">Member Dashboard Welcome Email — Preview</h2>
          <p className="text-[13px] leading-[1.5] text-[#727272]">Sent to {recipients.length} Primary Contacts who have not yet logged in. Fields in [ ] are filled in for each recipient.</p>
        </div>
        {editing && <p className="shrink-0 text-[13px] leading-[1.5] text-[#0A0A0A]">Click any outlined text to edit it. The subject, greeting, login credentials and {noun.singular} name are fixed and can&apos;t be edited.</p>}
        <div className={`flex min-h-0 flex-1 flex-col rounded-[10px] border border-[#ECECEC] ${editing ? "overflow-y-auto" : "overflow-hidden"}`}>
          <div className="shrink-0 space-y-[4px] border-b border-[#ECECEC] bg-[#F7F7F8] px-[18px] py-[14px] text-[13px] leading-[normal] text-[#0A0A0A]">
            <p><strong>To:</strong> <span>[Primary Contact Name]</span> &lt;<span>[Primary Contact Email]</span>&gt;</p>
            <p className="break-words"><strong>Subject:</strong> {plan?.subject}</p>
            <details>
              <summary className="cursor-pointer leading-[1.5]">View all {recipients.length} recipients and delivery details</summary>
              <div className="max-h-[160px] space-y-[4px] overflow-y-auto py-[8px] leading-[1.5]">
                <p className="break-words"><strong>From:</strong> {plan?.fromEmail}</p>
                {plan?.replyToEmail && <p className="break-words"><strong>Reply to:</strong> {plan.replyToEmail}</p>}
                <p><strong>CC:</strong> None · <strong>BCC:</strong> None</p>
                {plan && <MassCredentialExclusionSummary recipientCount={recipients.length} skippedCount={plan.skippedCount} skippedReasons={plan.skippedReasons} />}
                <ul>{recipients.map(row => <li key={row.memberId} className="break-words">{row.contactName} &lt;{row.actualRecipient}&gt;</li>)}</ul>
              </div>
            </details>
          </div>
          {editing
            ? <MassCredentialEmailEditor ref={editorRef} chamberName={chamberName} edits={editing.base} resetKey={editing.resetKey} />
            : <div className="min-h-0 flex-1 px-[18px] py-[20px]"><iframe title="Mass member welcome email" srcDoc={buildMemberCredentialEmailPreviewDocument(plan?.emailHtml || "")} sandbox="" className="h-full w-full border-0 bg-white" /></div>}
        </div>
        {editBusy && <p role="status" className="shrink-0 text-[13px] text-[#727272]">{editing ? "Saving your email changes..." : "Loading the email text..."}</p>}
        {editError && <p role="alert" className="shrink-0 text-[13px] text-[#E24C4C]">{editError}</p>}
        {busy && progress && <p role="status">{formatMassCredentialProgress(progress)}. Keep this page open.</p>}
        {(message || failure || previewFailure) && <p role="alert" className="shrink-0 text-[13px] text-[#E24C4C]">{failure || previewFailure || message}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-[12px] border-t border-[#ECECEC] bg-[#F7F7F8] px-[32px] py-[14px]">
        {editing ? <>
          <button type="button" disabled={editBusy} className="rounded-[8px] border border-[#9E9E9E] bg-white px-[22px] py-[11px] text-[14px] font-semibold leading-[normal] text-[#0A0A0A] hover:bg-[#F7F7F8]" onClick={cancelEditing}>Cancel</button>
          <DesignDialogButton className="!flex-none !px-[24px] !py-[11px] !leading-[normal]" disabled={editBusy} onClick={() => { void saveEditing(); }}>Save</DesignDialogButton>
        </> : <>
          <button type="button" disabled={busy || editBusy} className="rounded-[8px] border border-[#9E9E9E] bg-white px-[22px] py-[11px] text-[14px] font-semibold leading-[normal] text-[#0A0A0A] hover:bg-[#F7F7F8]" onClick={() => setMode("options")}>Back</button>
          <button type="button" disabled={busy || editBusy || !plan} className="inline-flex shrink-0 items-center gap-[8px] rounded-[8px] border border-[#0A0A0A] bg-white px-[16px] py-[9px] text-[13px] font-semibold leading-[normal] text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:cursor-not-allowed disabled:opacity-50" onClick={() => { void startEditing(); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
            Edit Email
          </button>
          {planFailed && <DesignDialogButton tone="dark" disabled={loading || busy} onClick={() => { void refreshPlan(); }}>Try Loading Again</DesignDialogButton>}
          {reviewRequired && <DesignDialogButton tone="dark" disabled={busy || loading || !recipients.length} onClick={reviewRemaining}>Review Remaining Contacts</DesignDialogButton>}
          <DesignDialogButton className="!flex-none !px-[24px] !py-[11px] !leading-[normal]" disabled={sendDisabled} onClick={() => requestSend()}>Send to All</DesignDialogButton>
        </>}
      </div>
      <MassCredentialDiscardConfirm open={!!discardAction} onKeepEditing={() => setDiscardAction(null)} onDiscard={discardEdits} />
      {/* Rendered inside the preview so the nested pop-up keeps focus. */}
      {mode === "preview" && <ActionSuccessDialog message={success?.message} note={success?.note} onClose={() => setSuccess(null)} />}
    </DesignDialog>
    {mode !== "preview" && <ActionSuccessDialog message={success?.message} note={success?.note} onClose={() => setSuccess(null)} />}
  </div>;
}
